import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../lib/auth-context.js';
import { HealthBanner } from './HealthBanner.js';
import { Logo } from './Logo.js';

interface NavItem {
  to: string;
  label: string;
  moduleKey?: string;
  group?: 'primary' | 'system';
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/pipeline', label: 'My Pipeline', moduleKey: 'pipeline_tracking', group: 'primary' },
  { to: '/workflows', label: 'Workflows', moduleKey: 'workflows', group: 'primary' },
  { to: '/mana-pricing', label: 'Mana Pricing', moduleKey: 'mana_pricing', group: 'primary' },
  { to: '/accounting', label: 'Accounting', moduleKey: 'accounting', group: 'primary' },
  { to: '/call-reports', label: 'Call Reports', moduleKey: 'call_reports', group: 'primary' },
  { to: '/reporting', label: 'Reporting', moduleKey: 'reporting', group: 'primary' },
  { to: '/crm', label: 'My CRM', moduleKey: 'crm', group: 'primary' },
  { to: '/admin-tasks', label: 'Admin Tasks', moduleKey: 'admin_tasks', group: 'primary' },
  { to: '/integrations', label: 'Integrations', group: 'system' },
  { to: '/admin', label: 'Administration', group: 'system', adminOnly: true },
];

export function Shell({ children }: { children: React.ReactNode }) {
  const { me, signOut } = useAuth();
  // Mobile: the sidebar is an off-canvas drawer toggled from the top bar.
  // Desktop (lg+): classic fixed two-column layout, drawer state ignored.
  const [navOpen, setNavOpen] = useState(false);
  // Mana Pricing's two-column workspace (scenario + results) uses the full width of large screens.
  const wide = useLocation().pathname.startsWith('/mana-pricing');
  const perms = new Map(me?.modulePermissions.map((p) => [p.moduleKey, p]));
  // Admin-tier check spans both `admin` and `super_admin` so super admins
  // see every UI surface admins do (plus the super-admin-only cards inside).
  const role = me?.user.role;
  const isAdmin = role === 'admin' || role === 'super_admin';

  // Only show modules the user can actually access (canView). Admin-tier bypasses
  // per-module checks, so admins / super-admins still see every module.
  const primary = NAV.filter(
    (n) => n.group === 'primary' && (isAdmin || !!perms.get(n.moduleKey ?? '')?.canView),
  );
  const system = NAV.filter((n) => n.group === 'system' && (!n.adminOnly || isAdmin));

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      'group flex items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors',
      isActive
        ? 'bg-white/10 text-white shadow-[inset_3px_0_0_theme(colors.brand.gold.500)]'
        : 'text-brand-navy-100 hover:bg-white/5 hover:text-white',
    );

  return (
    <div className="flex h-full flex-col bg-brand-cream lg:grid lg:grid-cols-[260px_1fr]">
      {/* Mobile top bar */}
      <header className="flex shrink-0 items-center justify-between bg-brand-navy-800 px-4 py-3 lg:hidden">
        <Logo variant="full" tone="navy" size={30} />
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setNavOpen(true)}
          className="rounded-md p-2 text-brand-navy-100 hover:bg-white/10 hover:text-white"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* Mobile backdrop behind the open drawer */}
      {navOpen && (
        <div
          aria-hidden
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col bg-brand-navy-800 text-brand-navy-100',
          'transition-transform duration-200 ease-out',
          navOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:z-auto lg:h-full lg:translate-x-0 lg:transition-none',
        )}
      >
        {/* Brand */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-5">
          <Logo variant="full" tone="navy" size={36} />
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
            className="rounded-md p-1.5 text-brand-navy-200 hover:bg-white/10 hover:text-white lg:hidden"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 text-sm">
          {primary.length > 0 && (
            <>
              <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-navy-300">
                Modules
              </div>
              <ul className="space-y-0.5">
                {primary.map((item) => {
                  const perm = item.moduleKey ? perms.get(item.moduleKey) : undefined;
                  const phase1 = perm?.phase1Enabled ?? false;
                  return (
                    <li key={item.to}>
                      <NavLink to={item.to} end onClick={() => setNavOpen(false)} className={linkClass}>
                        <span className="flex items-center gap-2">{item.label}</span>
                        {!phase1 && (
                          <span className="rounded-full bg-brand-navy-700/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-brand-gold-200">
                            Soon
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <div className="px-2 pb-2 pt-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-navy-300">
            System
          </div>
          <ul className="space-y-0.5">
            {system.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end onClick={() => setNavOpen(false)} className={linkClass}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User card */}
        <div className="border-t border-white/10 px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gold-500 text-sm font-semibold text-white">
              {me?.user.name?.slice(0, 1) ?? '?'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-white">{me?.user.name}</div>
              <div className="truncate text-[11px] text-brand-navy-300">
                {me?.user.email}
              </div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="rounded-full bg-brand-gold-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-gold-200">
              {me?.user.role}
            </span>
            <button
              onClick={signOut}
              className="rounded-md px-2 py-1 text-[11px] font-medium text-brand-navy-200 hover:bg-white/5 hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="min-h-0 flex-1 overflow-y-auto lg:h-full">
        <div className={clsx('mx-auto px-4 py-6 sm:px-6 lg:px-8 lg:py-8', wide ? 'max-w-[1800px]' : 'max-w-7xl')}>
          <HealthBanner />
          {children}
        </div>
      </main>
    </div>
  );
}
